/**
 * @file inward-timeline.ts
 * @module app/modules/inward/utils/inward-timeline
 * @description Times, durations and the overall status of an Inward GRN:
 *   Inward started -> Inward done (GRN saved) -> Warehouse and Finance approvals (parallel)
 *   -> Stock live (only after both approvals).
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import {
  InwardGrnRecord,
  isApproved,
  isGrnRejected,
  pendingShortfallCount,
} from "./inward-approval";

/** "2d 3h", "3h 05m", "12m", "45s" (negative or invalid -> "-"). */
export const formatDuration = (ms: number): string => {
  if (!Number.isFinite(ms) || ms < 0) return "-";
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, "0")}m`;
  if (minutes > 0) return `${minutes}m`;
  return `${totalSeconds}s`;
};

const toTime = (value?: string | Date | null) =>
  value ? new Date(value).getTime() : NaN;

/** Duration between two times; when `to` is missing, up to `now` (i.e. still waiting). */
export const durationBetween = (
  from?: string | Date | null,
  to?: string | Date | null,
  now: Date = new Date()
): string => {
  const start = toTime(from);
  if (Number.isNaN(start)) return "-";
  const end = to ? toTime(to) : now.getTime();
  return formatDuration(end - start);
};

/** "08/10/2026 14:05" in the user's local time zone. */
export const formatDateTime = (value?: string | Date | null): string => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

export type InwardStage =
  | "awaitingBoth"
  | "awaitingFinance"
  | "partlyLive"
  | "awaitingStock"
  | "waitingWarehouseForStock"
  | "stockLive"
  | "shortfallDecision"
  | "rejected";

/**
 * Overall stage. Nothing goes live before Warehouse verifies the GRN; after that each product
 * goes live as Finance approves it ("partlyLive" while some are still waiting).
 */
/** Approved / live product counts (from totals, or the stored lines on list records). */
export const lineCounts = (grn: InwardGrnRecord) => {
  const stored = grn.inward?.lines ?? [];
  return {
    approved:
      grn.totals?.financeApprovedLines ??
      stored.filter((l) => l.financeStatus === 2).length,
    live: grn.totals?.liveLines ?? stored.filter((l) => !!l.stockLiveAt).length,
    total:
      grn.totals?.lineCount ??
      grn.receivedLineCount ??
      stored.filter((l) => l.financeStatus).length,
  };
};

export const inwardStage = (grn: InwardGrnRecord): InwardStage => {
  const w = isApproved(grn.inward?.warehouseApproval);
  const f = isApproved(grn.inward?.financeApproval);
  const counts = lineCounts(grn);
  if (isGrnRejected(grn)) return "rejected";
  if (grn.inward?.stockLiveAt) return "stockLive";
  if (pendingShortfallCount(grn) > 0) return "shortfallDecision";
  if (w) {
    // Both done but not credited yet (stock update failed / in progress)
    if (f) return "awaitingStock";
    return counts.live > 0 ? "partlyLive" : "awaitingFinance";
  }
  return f || counts.approved > 0 ? "waitingWarehouseForStock" : "awaitingBoth";
};

export const STAGE_LABELS: Record<
  InwardStage,
  { label: string; variant: string }
> = {
  awaitingBoth: {
    label: "Waiting for Warehouse & Finance",
    variant: "neutral",
  },
  awaitingFinance: { label: "Waiting for Finance", variant: "warning" },
  partlyLive: { label: "Partly live · Finance pending", variant: "info" },
  awaitingStock: {
    label: "Approved · stock update pending",
    variant: "danger",
  },
  waitingWarehouseForStock: {
    label: "Finance approved · stock waits for Warehouse",
    variant: "warning",
  },
  stockLive: { label: "Stock live", variant: "success" },
  shortfallDecision: {
    label: "Missing / damaged to approve or reject",
    variant: "warning",
  },
  rejected: { label: "Rejected · back to PO", variant: "danger" },
};

export const ACTION_LABELS: Record<string, string> = {
  inwardStarted: "Inward started",
  inwardDone: "Inward done (GRN saved)",
  warehouseApproved: "Warehouse approved (photo proof)",
  financeApproved: "Finance approved (documents)",
  stockLive: "Stock live",
  costChangeRequested: "Cost price change requested",
  costChangeAcknowledged: "Cost price change acknowledged",
  sellingPriceUpdated: "Selling price updated (procurement)",
  grnRejected: "GRN rejected (quantities back to PO)",
  shortfallApproved: "Missing / damaged approved",
  shortfallRejected: "Missing / damaged rejected (back to PO)",
};

export interface TimelineStep {
  key: string;
  label: string;
  /** when it happened; empty while waiting */
  at?: string;
  by?: string;
  /** how long it took / has been waiting since the previous milestone */
  duration: string;
  done: boolean;
  waitingLabel?: string;
}

/** Steps shown on the GRN screen, with how long each took or has been waiting. */
export const buildTimeline = (
  grn: InwardGrnRecord,
  now: Date = new Date()
): TimelineStep[] => {
  const i = grn.inward ?? {};
  const doneAt = i.doneAt ?? grn._createdAt ?? grn.date;
  const w = i.warehouseApproval;
  const f = i.financeApproval;
  const wDone = isApproved(w);
  const fDone = isApproved(f);
  const stockAt = i.stockLiveAt ?? undefined;
  const counts = lineCounts(grn);
  return [
    {
      key: "started",
      label: "Inward started",
      at: i.startedAt,
      by: i.startedBy?.name,
      duration: "-",
      done: !!i.startedAt,
    },
    {
      key: "done",
      label: "Inward done (GRN saved)",
      at: doneAt,
      by: i.doneBy?.name,
      duration: i.startedAt ? durationBetween(i.startedAt, doneAt, now) : "-",
      done: !!doneAt,
    },
    {
      key: "warehouse",
      label: "Warehouse approval",
      at: wDone ? w?.at : undefined,
      by: wDone ? w?.byName : undefined,
      duration: durationBetween(doneAt, wDone ? w?.at : undefined, now),
      done: wDone,
      waitingLabel: "Waiting for Warehouse manager",
    },
    {
      key: "finance",
      label: fDone
        ? "Finance approval"
        : `Finance approval (${counts.approved} of ${counts.total} products)`,
      at: fDone ? f?.at : undefined,
      by: fDone ? f?.byName : undefined,
      duration: durationBetween(doneAt, fDone ? f?.at : undefined, now),
      done: fDone,
      waitingLabel: "Waiting for Finance manager",
    },
    {
      key: "stock",
      label: "Stock live",
      at: stockAt,
      duration: durationBetween(doneAt, stockAt, now),
      done: !!stockAt,
      waitingLabel:
        !wDone && (fDone || counts.approved > 0)
          ? "Finance approved — waiting for Warehouse approval"
          : wDone
          ? `${counts.live} of ${counts.total} products live`
          : "Waits for Warehouse and Finance",
    },
  ];
};
